import { KJUR } from 'jsrsasign';

import { isOktaTokenStorage } from '@/utils/auth';

import Constants from '@/constants';

import { queryClient } from '@/services/api';
import { OktaUserRaw } from '@/types';

import {
  permissionsAppliedToTokenAtom,
  permissionsAtom,
  permissionsOnActualTokenAtom,
  permissionsWidgetInitializedAtom,
  setSomePermissions,
} from './state';
import { setImpersonatedToken } from './token';

import { initializeWebUser } from '@/app/initializeUser';
import { store } from '@/atoms';
import { AccessTokenClaims } from '@/okta/okta-utils';
import oktaAuth from '@/okta/oktaConfig';

export const setPermissionsFromToken = (accessToken?: AccessTokenClaims) => {
  if (!accessToken) return;

  const permissions = Object.keys(accessToken.cfaPerms?.P20 ?? {});
  store.set(permissionsOnActualTokenAtom, permissions);
  setSomePermissions(
    permissions.reduce<Record<string, boolean>>(
      (acc, permission) => ({ ...acc, [permission]: true }),
      {},
    ),
  );

  const permsSet = store.get(permissionsAtom);
  store.set(permissionsAppliedToTokenAtom, permsSet);

  store.set(permissionsWidgetInitializedAtom, true);
};

export const onPermissionsUpdate = async () => {
  console.log('Permissions Widget: running permissions update...');

  const permissions = store.get(permissionsAtom);
  store.set(permissionsAppliedToTokenAtom, permissions);

  const fakedToken = getNewToken();
  setImpersonatedToken(fakedToken);

  const authState = oktaAuth.authStateManager.getAuthState();
  if (!authState) {
    throw new Error(
      'Permissions Widget: No auth state found, sorry! Doing nothing.',
    );
  }

  try {
    await initializeWebUser();
    queryClient.invalidateQueries({ queryKey: ['browse'] });
  } catch (error) {
    throw new Error(`Permissions Widget: ${error}`, { cause: error });
  }

  const permissionsOn = Object.entries(store.get(permissionsAtom))
    .filter(([_, toggle]) => toggle)
    .map(([permission]) => permission);

  const permissionsString = permissionsOn.join(', ');

  console.log(
    'Permissions Widget: permissions updated with',
    permissionsString,
  );
};

/**
 * Create a new access token based on new permissions if there are differences.
 */
const getNewToken = (): string | undefined => {
  const localStorageToken = localStorage.getItem('okta-token-storage');

  const rawStorage = localStorageToken
    ? (JSON.parse(localStorageToken) as unknown)
    : undefined;
  const storage = rawStorage
    ? isOktaTokenStorage(rawStorage)
      ? rawStorage
      : undefined
    : undefined;

  const tokenRaw = storage?.accessToken?.accessToken;

  if (
    Constants.ENV === 'prod' ||
    !tokenRaw ||
    !differencesExistInPermissions()
  ) {
    return undefined;
  }

  const parseResponse = KJUR.jws.JWS.parse(tokenRaw);

  const token = parseResponse.payloadObj as OktaUserRaw;

  const locations = (token as { cfa_loc_list?: string[] }).cfa_loc_list ?? [
    '00000',
  ];

  const selectedPermissions = Object.entries(
    store.get(permissionsAppliedToTokenAtom),
  )
    .filter(([_, toggle]) => toggle)
    .map(([permission]) => permission);

  return generateToken(token, locations, selectedPermissions);
};

const generateToken = (
  token: OktaUserRaw,
  locations: string[],
  permissions: string[],
) => {
  const tokenCopy = structuredClone(token);

  const newPermissions = permissions.reduce<Record<string, string[]>>(
    (acc, permission) => ({
      ...acc,
      [permission]: locations,
    }),
    {},
  );

  tokenCopy.cfa_perms.P20 = newPermissions;
  (tokenCopy as { cfa_loc_list?: string[] }).cfa_loc_list = locations;

  const header = { alg: 'HS256', typ: 'JWT' };
  const secret = 'Fake Pathway Token';

  const sHeader = JSON.stringify(header);
  const sPayload = JSON.stringify(tokenCopy);

  const mockedToken = KJUR.jws.JWS.sign('HS256', sHeader, sPayload, {
    utf8: secret,
  });

  return mockedToken;
};

const differencesExistInPermissions = () => {
  const permissionsAppliedToToken = store.get(permissionsAppliedToTokenAtom);

  const realTokenPerms = store
    .get(permissionsOnActualTokenAtom)
    .reduce<
      Record<string, boolean>
    >((acc, permission) => ({ ...acc, [permission]: true }), {});

  Object.entries(permissionsAppliedToToken).forEach(([perm, toggle]) => {
    if (!toggle && realTokenPerms[perm] === undefined) {
      realTokenPerms[perm] = false;
    }
  });

  const diff = countDifferences(realTokenPerms, permissionsAppliedToToken);

  return diff !== 0;
};

export const countDifferences = (
  obj1: Record<string, boolean>,
  obj2: Record<string, boolean>,
): number => {
  const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
  let count = 0;

  allKeys.forEach(key => {
    if (obj1[key] !== obj2[key]) {
      count++;
    }
  });

  return count;
};
